import { Box, CircularProgress, makeStyles, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { FC, useState, useMemo, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router";

import { Agent, AgentDetailAccessSourceEnum } from "@/api/generated";
import { AgentDetailLoadError } from "@/components/agent/AgentDetailLoadError";
import { AgentDetails } from "@/components/agent/AgentDetails";
import { AgentNotFound } from "@/components/agent/AgentNotFound";
import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import { MatchConfirmModal } from "@/components/offer/MatchConfirmModal";
import { PLANNER_MATCH_CTA_LABEL } from "@/constants/plannerMatching";
import { useAgentDetailAccessLog } from "@/hooks/useAgentDetailAccessLog";
import { useCreateUserProposal } from "@/hooks/useCreateUserProposal";
import { useDevice } from "@/hooks/useDevice";
import { useGetAgentDetails } from "@/hooks/useGetAgentDetails";
import { useObservedHeight } from "@/hooks/useObservedHeight";
import { useUpdatePreferredContactSchedules } from "@/hooks/useUpdatePreferredContactSchedules";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { sendEventToAppMeasurementTool, AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { DiagnosisPlannerLocationState, isDiagnosisPlannerFlow, readCompatibilityRank } from "@/utilities/diagnosisPlannerFlow";
import { NavigationPath } from "@/utilities/enums";
import { AGENT_DETAIL_URL_SOURCE, USER_OFFER_SOURCE } from "@/utilities/userOffer";

import { PlannerDetailSkeleton } from "./components/PlannerDetailSkeleton";

// 「話を聞いてみる」の送信失敗。無反応だと「押したのに何も起きなかった」に見えて離脱か連打を招くため、
// 一覧側の CTA と同じ文言で必ずフィードバックを出す。
// 原因は通信・認証・サーバのいずれもありうるので断定しない。
// 「もう一度お試しください」とは書かない。サーバ側の UserProposal は重複防止が無効化されており
// （user_proposal_concern の二重登録チェックはコメントアウト、DB の一意制約も撤去済み）、
// 「サーバは成功したがクライアントには失敗に見えた」ケースで再送すると
// オファーとエージェント宛メールが二重に発生する。事実だけを伝え、再操作はユーザーの判断に委ねる
const PROPOSAL_ERROR_MESSAGE = "送信に失敗しました。マッチ状況をご確認ください。";
// セッション切れは何度押しても回復しないため、再試行ではなく再ログインへ誘導する
// （マスタ取得側の 401 文言と揃える）
const PROPOSAL_UNAUTHORIZED_MESSAGE = "送信に失敗しました。再度ログインしてください。";

const useStyles = makeStyles(() => ({
  // BaseTemplate の下余白を外すため、PC では元のページ 32px + main 16px をここで確保する。
  page: { [theme.breakpoints.up("md")]: { paddingTop: 32, paddingBottom: 48 } },
  pageHead: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: theme.palette.primary.main,
    fontSize: 14,
    background: "none",
    border: 0,
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    alignSelf: "start",
  },
  description: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  actions: {
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    alignItems: "center",
    "& button": { width: "100%", [theme.breakpoints.up("md")]: { width: 360, maxWidth: "100%" } },
    [theme.breakpoints.up("md")]: { padding: 0 },
  },
  floatingActions: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.appBar,
    padding: "16px 24px calc(24px + env(safe-area-inset-bottom, 0px))",
    backgroundColor: theme.palette.semantic["surface/card"],
    boxShadow: "0 -2px 8px rgba(22,32,46,0.08)",
    maxHeight: "calc(100% - 56px)",
    overflowY: "auto",
  },
  actionsSpacer: { height: "calc(88px + env(safe-area-inset-bottom, 0px))" },
  loading: {
    minHeight: "calc(100vh - 56px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 16,
  },
}));

export const AgentDetailPage: FC = () => {
  const { id } = useParams<{ id?: string }>();
  // PC詳細ペインと同様、送信中の処理・モーダル・希望日程の状態を対象IDに閉じ込める。
  // URLだけが変わる遷移でも旧画面を離脱させ、遅い送信結果を新しい相手に表示しない。
  return <AgentDetailPageContent key={id} />;
};

const AgentDetailPageContent: FC = () => {
  const history = useHistory();
  const location = useLocation<DiagnosisPlannerLocationState>();
  const isDiagnosisFlow = isDiagnosisPlannerFlow(location);
  // 相性プランナー一覧（診断結果・ホーム）と検索一覧から来たときは一覧での順位を引き継ぎ、上位3人に王冠を出す。
  // オファー・メッセージ・直リンクなど一覧を経由しない経路には順位が無いので王冠は付かない
  const compatibilityRank = readCompatibilityRank(location.state);

  const { id } = useParams<{ id?: string }>();
  const classes = useStyles();
  const { isMobile } = useDevice();
  const { ref: actionsRef, height: actionsHeight } = useObservedHeight<HTMLDivElement>({ enabled: isMobile });

  // URLパラメータからsourceを取得
  const params = new URLSearchParams(location.search);
  const urlSource = params.get("source") || "";

  // API送信用のsourceを決定（詳細画面経由パターン）
  const getApiSource = () => {
    switch (urlSource) {
      case AGENT_DETAIL_URL_SOURCE.TOP:
        return USER_OFFER_SOURCE.TOP_VIA_DETAIL;
      case AGENT_DETAIL_URL_SOURCE.AGENT_SEARCH:
        return USER_OFFER_SOURCE.AGENT_SEARCH_VIA_DETAIL;
      case AGENT_DETAIL_URL_SOURCE.SEARCH_RESULT:
        return USER_OFFER_SOURCE.SEARCH_RESULT_VIA_DETAIL;
      case AGENT_DETAIL_URL_SOURCE.LIMITED_PROPERTY:
        return USER_OFFER_SOURCE.LIMITED_PROPERTY_VIA_DETAIL;
      case AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE:
        return USER_OFFER_SOURCE.LEARNING_ARTICLE_VIA_DETAIL;
      case AGENT_DETAIL_URL_SOURCE.SIMILAR_AGENT:
        return USER_OFFER_SOURCE.SIMILAR_AGENT_VIA_DETAIL;
      case AGENT_DETAIL_URL_SOURCE.DIAGNOSIS_COMPATIBILITY_AGENTS:
        return USER_OFFER_SOURCE.DIAGNOSIS_COMPATIBILITY_AGENTS_VIA_DETAIL;
      default:
        return "";
    }
  };

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  // マッチは取り消せずプランナーへも通知が飛ぶため、実行前に確認モーダルを挟む
  const [isMatchConfirmModalOpen, setIsMatchConfirmModalOpen] = useState(false);
  React.useEffect(() => {
    // 詳細タップ・申請成功の既存イベントと同じ登録導線に限定する。
    // 追加おすすめは別 source のため含めず、モーダルが開いた時だけ送る。
    if (isMatchConfirmModalOpen && urlSource === AGENT_DETAIL_URL_SOURCE.DIAGNOSIS_COMPATIBILITY_AGENTS) {
      sendEventToAppMeasurementTool(AppEventTokenEnum.RecommendedPlannerMatchConfirmAccess);
    }
  }, [isMatchConfirmModalOpen, urlSource]);

  const [additionalAgents, setAdditionalAgents] = useState<Agent[]>([]);
  // マッチ成立後の希望連絡日程 PATCH に使う UserProposal の ID
  const [userProposalId, setUserProposalId] = useState<number | undefined>(undefined);
  // 対象条件に合致せずプランナーの承諾待ちになるマッチかどうか（完了モーダルの出し分けに使う）
  const [isOutOfTargetCondition, setIsOutOfTargetCondition] = useState<boolean | undefined>(undefined);

  const { fetchAgentById, agent, loading: fetching, notFound, error } = useGetAgentDetails(Number(id));

  const { createUserProposal, loading: approving } = useCreateUserProposal();
  const { updatePreferredContactSchedules } = useUpdatePreferredContactSchedules();
  const { enqueueSnackbar } = useSnackbar();

  // 送信・再取得の待ち中に画面を離れうる。React 17 ではアンマウント後の setState が console.error 警告になり、
  // src/index.tsx の CaptureConsole 経由で Sentry イベントになるため、await をまたいだ更新はマウント中に限る
  // （useAgentSearchList / useCompatibilityAgents の mountedRef と同じ扱い）。
  // StrictMode 無しの React 17 では再マウント＝別インスタンス＝別の ref になるため、
  // false のまま残って以降 setState されなくなる事故は起きない（この ref に外部入力は届かない）
  const mountedRef = useRef(true);
  React.useEffect(() => {
    // effect が張り直されたときに false のまま残らないよう、本体で戻す
    // （React 17 かつ StrictMode 無しの現状は不要だが、Fast Refresh と将来の React 18 化への保険）
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useAgentDetailAccessLog(Number(id), urlSource as AgentDetailAccessSourceEnum);

  const [initialized, setInitialized] = useState(false);
  const loading = useMemo(() => !initialized || fetching || approving, [initialized, fetching, approving]);

  React.useEffect(() => {
    if (isDiagnosisFlow && error) history.replace("/mypage");
  }, [error, history, isDiagnosisFlow]);

  // 話を聞いてみるボタンの押下時
  const handleClickModalApprove = async () => {
    window.scrollTo(0, 0);
    const {
      ok,
      additionalAgents: recommendedAgents,
      isTarget,
      isOutOfTargetCondition: createdIsOutOfTargetCondition,
      isUnauthorized,
      userProposalId: createdUserProposalId,
    } = await createUserProposal({
      agent_id: Number(id),
      source: getApiSource(),
    });
    // 成功・失敗いずれでも確認モーダルは閉じる（失敗時は CTA から再操作できる）
    if (mountedRef.current) setIsMatchConfirmModalOpen(false);
    if (ok) {
      // 表示の更新はマウント中だけ行う。計測イベントは画面表示に依存しない副作用なので、
      // 離脱後でも従来どおり発火させる（送信は成立しているため計測から欠落させない）
      if (mountedRef.current) {
        setAdditionalAgents(recommendedAgents);
        setUserProposalId(createdUserProposalId);
        setIsOutOfTargetCondition(createdIsOutOfTargetCondition);
      }
      // adjust向けにマッチングイベント発火
      sendEventToAppMeasurementTool(AppEventTokenEnum.SendUserProposal);
      if (isTarget) {
        sendEventToAppMeasurementTool(AppEventTokenEnum.SendUserProposalTargetMatched);
      }
      // おすすめプランナー一覧→詳細→マッチの導線を切り分けるため、既存イベントに加えて発火する
      if (urlSource === AGENT_DETAIL_URL_SOURCE.DIAGNOSIS_COMPATIBILITY_AGENTS) {
        sendEventToAppMeasurementTool(AppEventTokenEnum.RecommendedPlannerDetailMatch);
      }

      // マッチングのモーダル表示
      if (mountedRef.current) setIsApproveModalOpen(true);
      // マッチ状態(is_matched)を反映するため詳細を再取得する。
      // 離脱後は再取得しても表示先が無いうえ、useGetAgentDetails 側も setState するため走らせない
      if (mountedRef.current) await fetchAgentById();
    } else {
      if (isDiagnosisFlow) {
        if (mountedRef.current) history.replace("/mypage");
        return;
      }
      // 失敗しても押せる状態に戻るだけでは無反応に見えるため、送信できなかったことを明示して再操作を促す。
      // snackbar の表示先はアプリ全体の Provider なので、離脱後でも警告にはならずガードは不要
      // Provider が preventDuplicate なので、同じ文言の2回目以降が握り潰される。
      // 連続して失敗したときも毎回伝えたいので、この通知だけ重複抑止を外す
      enqueueSnackbar(isUnauthorized ? PROPOSAL_UNAUTHORIZED_MESSAGE : PROPOSAL_ERROR_MESSAGE, {
        preventDuplicate: false,
      });
    }
  };

  React.useEffect(() => {
    setInitialized(true);
  }, []);

  const onBack = () => {
    if (isDiagnosisFlow) history.replace("/diagnosis_compatibility_agents/result");
    else if (history.length > 1) history.goBack();
    else history.replace("/agent_search");
  };
  const returnToSearch = () => {
    const returnTo = location.state?.searchReturnTo;
    history.push(returnTo && /^\/agent_search(?:\?|$)/.test(returnTo) ? returnTo : "/agent_search");
  };
  const actions = (
    <div
      ref={actionsRef}
      className={`${classes.actions} ${isMobile ? classes.floatingActions : ""}`}
      data-testid={isMobile ? "planner-floating-actions" : "planner-page-actions"}
    >
      <CustomButton
        tone="trust"
        buttonSize="L"
        fullWidth
        onClick={() => setIsMatchConfirmModalOpen(true)}
        disabled={agent?.is_matched}
      >
        {agent?.is_matched ? "マッチング済み" : PLANNER_MATCH_CTA_LABEL}
      </CustomButton>
    </div>
  );
  const content = (
    <>
      {loading ? (
        isMobile ? (
          <Box className={classes.loading} data-testid="loading">
            <CircularProgress size={40} />
            <Typography className={classes.description}>読み込んでいます</Typography>
          </Box>
        ) : (
          <PlannerDetailSkeleton />
        )
      ) : notFound ? (
        <AgentNotFound
          onBack={() => history.replace(isDiagnosisFlow ? "/diagnosis_compatibility_agents/result" : "/agent_search")}
        />
      ) : error || !agent ? (
        <AgentDetailLoadError
          onRetry={fetchAgentById}
          onBack={() => history.replace(isDiagnosisFlow ? "/diagnosis_compatibility_agents/result" : "/agent_search")}
        />
      ) : (
        <Box className={classes.page}>
          {!isMobile && (
            <Box className={classes.pageHead}>
              <button className={classes.back} onClick={isDiagnosisFlow ? onBack : returnToSearch}>
                <Chevron direction="back" color="primary" />
                {isDiagnosisFlow ? "プランナー一覧へ戻る" : "認定プランナーを探す"}
              </button>
            </Box>
          )}
          <AgentDetails agent={agent} variant="certified" compatibilityRank={compatibilityRank} desktopColumns={!isMobile} sidebarActions={!isMobile ? actions : undefined}>
            {isMobile && (
              <Box className={classes.actionsSpacer} style={{ height: actionsHeight || undefined }} aria-hidden="true" data-testid="planner-actions-spacer" />
            )}
            {isMobile && actions}
          </AgentDetails>
        </Box>
      )}
      {isMatchConfirmModalOpen && (
        <MatchConfirmModal
          variant="planner"
          onConfirm={handleClickModalApprove}
          onCancel={() => setIsMatchConfirmModalOpen(false)}
        />
      )}
      {isApproveModalOpen && (
        <OfferApproveModal
          variant="planner"
          offerType="user_offer"
          agentId={Number(id)}
          additionalAgents={additionalAgents}
          isOutOfTargetCondition={isOutOfTargetCondition}
          onClickAdditionalAgent={(recommendedAgent) => {
            setIsApproveModalOpen(false);
            setAdditionalAgents([]);
            setUserProposalId(undefined);
            setIsOutOfTargetCondition(undefined);
            // 順位はこの画面のプランナーのものなので、追加おすすめの詳細には引き継がない（別人に王冠が付いてしまう）
            history.push(
              `/agent_search/${recommendedAgent.id}?source=${AGENT_DETAIL_URL_SOURCE.SIMILAR_AGENT}`,
              { ...location.state, compatibilityRank: undefined, fromDiagnosisCompatibilityAgents: isDiagnosisFlow }
            );
          }}
          onClose={() => {
            setIsApproveModalOpen(false);
            setAdditionalAgents([]);
            setUserProposalId(undefined);
            setIsOutOfTargetCondition(undefined);
          }}
          // ID が取れないときは日程を保存できないため配線しない（モーダル側が閉じられる挙動に落ちる）
          onSubmitPreferredContactSchedules={
            userProposalId
              ? async (request) => {
                  const ok = await updatePreferredContactSchedules("user_offer", userProposalId, request);
                  if (!ok && isDiagnosisFlow && mountedRef.current) history.replace("/mypage");
                  return ok;
                }
              : undefined
          }
        />
      )}
    </>
  );
  return (
    <BaseTemplate
      headerTitle={isMobile ? "認定プランナー詳細" : undefined}
      onBack={isMobile ? onBack : undefined}
      isBackGroundWhite={isMobile}
      isPcV2
      isDense
      disableBottomPadding
      hideGlobalNavigation={isDiagnosisFlow}
      navigationPath={isMobile ? undefined : NavigationPath.AGENT_SEARCH}
    >
      {content}
    </BaseTemplate>
  );
};
